import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const parseValue = (v) => {
  if (v == null || v === "") return 0;
  const cleaned = String(v)
    .replace(/\s/g, "")      // remove spaces
    .replace(",", ".")       // decimal comma -> dot
    .replace(/[^\d.-]/g, ""); // keep only digits, dot, minus

  const n = parseFloat(cleaned);
  return isNaN(n) || n < 0 ? 0 : n;
};


const formatNumber = (value) =>
  Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const GREEN = "#2e7d32";
const GREEN_BORDER = "#2e7d32";
const ORANGE = "#fb8c00";
const ORANGE_BORDER = "#fb8c00";

// === Styled ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const TopContractorsByTransactionType = ({ salesData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const availableYears = useMemo(
    () => [...new Set(salesData.map((row) => row.Rok.toString()))].sort(),
    [salesData]
  );

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears.includes("2024") ? "2024" : availableYears[0]);
    }
  }, [availableYears, selectedYear]);

const topContractors = useMemo(() => {
  if (!selectedYear) return [];

  const contractorTotals = {};

  salesData
    .filter(
      (row) =>
        row.Rok.toString() === selectedYear &&
        (selectedMonth === 0 || parseInt(row.Miesiac) === selectedMonth)
    )
    .forEach((row) => {
      const id = row.NrKontrahenta?.trim();
      const name = row.NazwaKontrahenta?.trim();
      if (!id || !name) return;

      if (!contractorTotals[id]) {
        contractorTotals[id] = {
          name,
          service: 0,
          other: 0,
        };
      }

      // === NEW RULES aligned with SpzedazData ===

      // 1) Service = K_12
      const service = parseValue(row.K_12);

      // 2) Total amount = all K_ used in SpzedazData.Amount
      const total =
        parseValue(row.K_15) +
        parseValue(row.K_19) +
        parseValue(row.K_10) +
        parseValue(row.K_21) +
        parseValue(row.K_17) +
        parseValue(row.K_11) +
        parseValue(row.K_22) +
        parseValue(row.K_13) +
        parseValue(row.K_14) +
        parseValue(row.K_12);

      // 3) Other = everything except service
      const other = Math.max(0, total - service);

      contractorTotals[id].service += service;
      contractorTotals[id].other += other;
    });

  return Object.entries(contractorTotals)
    .map(([contractorID, values]) => ({
      contractorID,
      name: values.name,
      service: values.service,
      other: values.other,
      total: values.service + values.other,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);
}, [salesData, selectedYear, selectedMonth]);


  const labels = topContractors.map((c) =>
    c.name.length > 30 ? `${c.name.slice(0, 30)}…` : c.name
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: "Service",
        data: topContractors.map((c) => c.service),
        backgroundColor: GREEN,          // uniform green
        borderColor: GREEN_BORDER,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Other",
        data: topContractors.map((c) => c.other),
        backgroundColor: ORANGE,         // uniform orange
        borderColor: ORANGE_BORDER,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  if (!selectedYear || availableYears.length === 0) {
    return <div style={{ textAlign: "center" }}>Ładowanie danych...</div>;
  }

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Najwięksi kontrahenci – Service vs Other
        <InfoIconWrapper data-tooltip-id="top-contractors-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="top-contractors-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Najwięksi kontrahenci</strong>
            <br />
            Wykres pokazuje top 10 kontrahentów, podział na: Service (zielony) i Other (pomarańczowy).
          </>
        }
      />

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Rok:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Miesiąc:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "1000px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            indexAxis: "y",
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  // Legend chips enforce Service = green, Other = orange
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    return ds.map((d, i) => ({
                      text: d.label,
                      fillStyle: i === 0 ? GREEN : ORANGE,
                      strokeStyle: i === 0 ? GREEN_BORDER : ORANGE_BORDER,
                      lineWidth: 1,
                      hidden: !chart.isDatasetVisible(i),
                      datasetIndex: i,
                    }));
                  },
                },
              },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${formatNumber(ctx.raw)}`,
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" },
                },
                ticks: {
                  callback: (value) => formatNumber(value),
                },
              },
              y: { stacked: true },
            },
          }}
        />
      </div>
    </div>
  );
};

export default TopContractorsByTransactionType;
